import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import useSEO, { trackEvent } from '@/hooks/useSEO';
import { Loader2, Check, Sparkles, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLES = `
.cohap{background:#FAF7F0;color:#0D1B2A;min-height:100vh;font-family:Inter,system-ui,sans-serif;}
.cohap-nav{padding:24px 48px;border-bottom:1px solid rgba(200,168,75,.2);background:#fff;display:flex;justify-content:space-between;align-items:center;}
.cohap-back{font-size:11px;font-weight:700;letter-spacing:.18em;text-transform:uppercase;color:#3A4A5C;cursor:pointer;border:none;background:none;padding:0;display:inline-flex;align-items:center;gap:8px;}
.cohap-back:hover{color:#C8A84B;}
.cohap-logo{font-family:'Cormorant Display',serif;font-size:22px;font-weight:700;letter-spacing:.04em;color:#C8A84B;}
.cohap-logo em{font-style:italic;font-weight:300;color:#0D1B2A;}

.cohap-shell{max-width:920px;margin:0 auto;padding:64px 32px 96px;}
.cohap-eyebrow{font-size:10px;font-weight:700;letter-spacing:.32em;text-transform:uppercase;color:#C8A84B;margin-bottom:14px;display:flex;align-items:center;gap:12px;}
.cohap-eyebrow::before{content:'';display:block;width:32px;height:1px;background:#C8A84B;}
.cohap-h1{font-family:'Cormorant Display',serif;font-size:clamp(44px,5.4vw,68px);font-weight:700;line-height:1.02;letter-spacing:-.025em;margin:0 0 18px;}
.cohap-h1 em{font-style:italic;color:#C8A84B;}
.cohap-lead{font-family:'Cormorant Display',serif;font-style:italic;font-size:clamp(17px,1.7vw,21px);color:#3A4A5C;line-height:1.55;font-weight:300;max-width:680px;margin:0 0 8px;}
.cohap-meta{display:flex;flex-wrap:wrap;gap:10px;margin:24px 0 12px;}
.cohap-pill{font-size:10px;font-weight:700;letter-spacing:.18em;text-transform:uppercase;padding:7px 14px;border-radius:50px;background:rgba(200,168,75,.08);color:#C8A84B;border:1px solid rgba(200,168,75,.32);}

/* Stepper */
.cohap-steps{display:flex;align-items:center;gap:10px;margin:36px 0 28px;flex-wrap:wrap;}
.cohap-step{display:flex;align-items:center;gap:8px;font-size:11px;font-weight:700;letter-spacing:.06em;color:rgba(13,27,42,.4);}
.cohap-step.active{color:#0D1B2A;}
.cohap-step.done{color:#10b981;}
.cohap-step-dot{width:24px;height:24px;border-radius:50%;border:1px solid rgba(13,27,42,.18);display:flex;align-items:center;justify-content:center;font-size:11px;background:#fff;}
.cohap-step.active .cohap-step-dot{border-color:#C8A84B;background:#C8A84B;color:#0D1B2A;}
.cohap-step.done .cohap-step-dot{border-color:#10b981;background:#10b981;color:#fff;}
.cohap-step-line{flex:1;height:1px;background:rgba(13,27,42,.12);min-width:24px;max-width:60px;}
.cohap-step.done + .cohap-step-line, .cohap-step.active + .cohap-step-line{background:#C8A84B;}

/* Card */
.cohap-card{background:#fff;border:1px solid rgba(200,168,75,.2);border-left:3px solid #C8A84B;padding:44px 48px;}
.cohap-card-h{font-family:'Cormorant Display',serif;font-size:30px;font-weight:700;line-height:1.1;letter-spacing:-.02em;margin:0 0 6px;}
.cohap-card-h em{font-style:italic;color:#C8A84B;}
.cohap-card-sub{font-size:13px;color:#3A4A5C;margin:0 0 28px;line-height:1.6;}
.cohap-row{display:grid;grid-template-columns:1fr 1fr;gap:18px;margin-bottom:18px;}
.cohap-row.full{grid-template-columns:1fr;}
.cohap-field{display:flex;flex-direction:column;gap:6px;}
.cohap-field label{font-size:10px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;color:#3A4A5C;}
.cohap-field label .req{color:#C8A84B;margin-left:4px;}
.cohap-field input,.cohap-field select,.cohap-field textarea{font-family:Inter,system-ui,sans-serif;font-size:14px;color:#0D1B2A;background:#FAF7F0;border:1px solid rgba(13,27,42,.14);padding:12px 14px;border-radius:2px;outline:none;transition:border-color .2s,background .2s;}
.cohap-field input:focus,.cohap-field select:focus,.cohap-field textarea:focus{border-color:#C8A84B;background:#fff;}
.cohap-field textarea{min-height:96px;resize:vertical;line-height:1.5;}
.cohap-field-hint{font-size:11px;color:#7A8190;font-style:italic;}
.cohap-field-counter{font-size:10px;color:#7A8190;text-align:right;}

/* Actions */
.cohap-actions{display:flex;justify-content:space-between;align-items:center;gap:12px;margin-top:32px;padding-top:24px;border-top:1px solid rgba(200,168,75,.18);}
.cohap-btn{display:inline-flex;align-items:center;gap:8px;font-size:11px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;padding:14px 28px;border-radius:50px;border:1px solid;cursor:pointer;transition:all .25s;background:transparent;}
.cohap-btn.primary{background:#0D1B2A;color:#C8A84B;border-color:#0D1B2A;}
.cohap-btn.primary:hover:not(:disabled){background:#C8A84B;color:#0D1B2A;border-color:#C8A84B;}
.cohap-btn.line{background:transparent;color:#0D1B2A;border-color:rgba(13,27,42,.18);}
.cohap-btn.line:hover:not(:disabled){border-color:#C8A84B;color:#C8A84B;}
.cohap-btn:disabled{opacity:.4;cursor:not-allowed;}

/* Success */
.cohap-success{text-align:center;padding:60px 32px;}
.cohap-success-icon{width:80px;height:80px;border-radius:50%;background:rgba(16,185,129,.1);border:2px solid #10b981;display:flex;align-items:center;justify-content:center;margin:0 auto 24px;color:#10b981;}
.cohap-success-h{font-family:'Cormorant Display',serif;font-size:46px;font-weight:700;line-height:1.05;letter-spacing:-.02em;margin:0 0 14px;}
.cohap-success-h em{font-style:italic;color:#C8A84B;}
.cohap-success-p{font-size:15px;color:#3A4A5C;line-height:1.7;max-width:520px;margin:0 auto 30px;}
.cohap-success-meta{font-size:12px;color:#7A8190;font-style:italic;margin-top:18px;letter-spacing:.04em;}

.cohap-error{background:rgba(239,68,68,.05);border:1px solid rgba(239,68,68,.3);color:#ef4444;padding:14px 18px;font-size:13px;margin-bottom:18px;border-radius:2px;}

@media(max-width:768px){
  .cohap-nav{padding:18px 24px;}
  .cohap-shell{padding:36px 20px 64px;}
  .cohap-card{padding:32px 24px;}
  .cohap-row{grid-template-columns:1fr;gap:14px;}
}
`;

const STEPS = [
  { id: 1, label: 'Founder' },
  { id: 2, label: 'Venture' },
  { id: 3, label: 'Why UAE' },
];

const FUNDING_STAGES = [
  'Idea / Pre-product', 'Pre-Seed', 'Seed', 'Series A', 'Series B+',
  'Bootstrapped Profitable', 'Other',
];

const INDUSTRIES = [
  'AI / Machine Learning', 'AI / Legal Tech', 'Web4 / Robotics', 'Real Estate / PropTech',
  'FinTech / Capital Markets', 'GovTech / RegTech', 'HealthTech', 'EdTech',
  'Climate / Sustainability', 'Consumer / Marketplace', 'Logistics / Mobility',
  'Sovereign / Defence', 'Other',
];

export default function CohortApply() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useSEO({
    title: "Apply to Cohort 1 · Startup Accelerator Dubai · July 2026 | SKI AXIS",
    description: "Apply to SKI AXIS Cohort 1 — Dubai's governance-first startup accelerator programme. 12 weeks, 20 chosen founders, free UAE incorporation consultancy via IFZA (advisory only · licence & visa fees not included), AI-powered evaluation, cross-border capital access. Applications open.",
    keywords: "apply for accelerator UAE, Dubai startup accelerator application, SKI Axis cohort apply, business accelerator Dubai 2026, accelerator programme Dubai 2026, governance accelerator application, startup UAE free zone, IFZA company registration accelerator, Dubai Silicon Oasis accelerator",
    canonical: "/cohort-1/apply",
    ogImage: "/og-cohort.png",
  });
  const [form, setForm] = useState({
    founder_name: '',
    founder_email: '',
    company_name: '',
    linkedin_url: '',
    website: '',
    region: '',
    industry: '',
    funding_stage: '',
    team_size: 1,
    one_liner: '',
    problem: '',
    solution: '',
    target_market: '',
    traction: '',
    why_uae: '',
    governance_focus: '',
    additional_links: '',
  });

  useEffect(() => { document.title = 'Apply for Cohort 1 · SKI AXIS'; }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = (s) => {
    setError('');
    if (s === 1) {
      if (!form.founder_name.trim() || !form.founder_email.trim() || !form.company_name.trim()) {
        setError('Please complete name, email, and company.');
        return false;
      }
      if (!/.+@.+\..+/.test(form.founder_email)) { setError('Please enter a valid email.'); return false; }
    }
    if (s === 2) {
      if (!form.industry || !form.funding_stage || !form.one_liner.trim() || !form.problem.trim() || !form.solution.trim() || !form.target_market.trim()) {
        setError('Please fill in industry, stage, one-liner, problem, solution and target market.');
        return false;
      }
    }
    if (s === 3) {
      if (!form.why_uae.trim() || form.why_uae.trim().length < 40) {
        setError('Please share at least a couple of sentences on why the UAE matters for your venture.');
        return false;
      }
    }
    return true;
  };

  const next = () => { if (validate(step)) setStep((s) => Math.min(3, s + 1)); };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!validate(3)) return;
    setSubmitting(true);
    setError('');
    try {
      const payload = { ...form, team_size: parseInt(form.team_size || 1, 10) || 1 };
      await axios.post(`${API}/cohort/applications`, payload);
      setSubmitted(true);
      trackEvent('cohort_application_submit', { region: form.region, funding_stage: form.funding_stage, industry: form.industry });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="cohap" data-testid="cohap-success">
        <style>{STYLES}</style>
        <div className="cohap-nav">
          <button className="cohap-back" onClick={() => navigate('/')}><ChevronLeft className="h-3.5 w-3.5" />Back to SKI AXIS</button>
          <div className="cohap-logo">SKI <em>AXIS</em></div>
        </div>
        <div className="cohap-shell">
          <div className="cohap-card cohap-success">
            <div className="cohap-success-icon"><Check className="h-10 w-10" /></div>
            <h1 className="cohap-success-h">Application <em>received.</em></h1>
            <p className="cohap-success-p">
              Thank you, <strong>{form.founder_name.split(' ')[0]}</strong>. Your Cohort 1 application for <strong>{form.company_name}</strong> is now in our review queue.
              Within 7 days you'll hear back with one of three outcomes — fast-track interview, watchlist, or feedback.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button className="cohap-btn primary" onClick={() => navigate('/events/invest-uae-forum-2026')} data-testid="cohap-success-forum">Reserve Forum Seat (31 May) →</button>
              <button className="cohap-btn line" onClick={() => navigate('/')} data-testid="cohap-success-home">Back to home</button>
            </div>
            <div className="cohap-success-meta">
              A confirmation email has been sent to <strong>{form.founder_email}</strong> · Cohort 1 begins <strong>1 July 2026</strong>.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cohap" data-testid="cohap-page">
      <style>{STYLES}</style>
      <div className="cohap-nav">
        <button className="cohap-back" onClick={() => navigate('/')} data-testid="cohap-nav-back"><ChevronLeft className="h-3.5 w-3.5" />Back to SKI AXIS</button>
        <div className="cohap-logo">SKI <em>AXIS</em></div>
      </div>

      <div className="cohap-shell">
        <div className="cohap-eyebrow">Cohort 1 · Starts 1 July 2026</div>
        <h1 className="cohap-h1">Apply to <em>Cohort 1.</em></h1>
        <p className="cohap-lead">Twenty hand-picked founders. Six weeks. Free UAE incorporation consultancy via IFZA + 40 free zones <span style={{ opacity: 0.75 }}>(advisory only · government licence &amp; visa fees not included)</span>. A governance, capital, and AI-readiness sprint that ends on demo-day with 50+ curated UAE / UK investors in the room.</p>
        <div className="cohap-meta">
          <span className="cohap-pill">No application fee</span>
          <span className="cohap-pill">AI-Reviewed in 7 Days</span>
          <span className="cohap-pill">Globally Sourced</span>
        </div>

        <div className="cohap-steps">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.id}>
              <div className={`cohap-step ${step === s.id ? 'active' : step > s.id ? 'done' : ''}`} data-testid={`cohap-step-${s.id}`}>
                <span className="cohap-step-dot">{step > s.id ? <Check className="h-3 w-3" /> : s.id}</span>
                <span>{s.label}</span>
              </div>
              {i < STEPS.length - 1 && <div className="cohap-step-line" />}
            </React.Fragment>
          ))}
        </div>

        <div className="cohap-card">
          {error && <div className="cohap-error" data-testid="cohap-error">{error}</div>}

          {step === 1 && (
            <>
              <h2 className="cohap-card-h">Tell us about <em>you</em>.</h2>
              <p className="cohap-card-sub">Who's at the helm of the venture you're building.</p>

              <div className="cohap-row">
                <div className="cohap-field">
                  <label>Full name<span className="req">*</span></label>
                  <input data-testid="cohap-name" value={form.founder_name} onChange={(e) => update('founder_name', e.target.value)} placeholder="Your full name" />
                </div>
                <div className="cohap-field">
                  <label>Email<span className="req">*</span></label>
                  <input data-testid="cohap-email" type="email" value={form.founder_email} onChange={(e) => update('founder_email', e.target.value)} placeholder="you@company.com" />
                </div>
              </div>

              <div className="cohap-row">
                <div className="cohap-field">
                  <label>Company name<span className="req">*</span></label>
                  <input data-testid="cohap-company" value={form.company_name} onChange={(e) => update('company_name', e.target.value)} placeholder="Madinat AI" />
                </div>
                <div className="cohap-field">
                  <label>Country / Region</label>
                  <input data-testid="cohap-region" value={form.region} onChange={(e) => update('region', e.target.value)} placeholder="United Kingdom · UAE · Singapore…" />
                </div>
              </div>

              <div className="cohap-row">
                <div className="cohap-field">
                  <label>LinkedIn URL</label>
                  <input data-testid="cohap-linkedin" value={form.linkedin_url} onChange={(e) => update('linkedin_url', e.target.value)} placeholder="https://linkedin.com/in/…" />
                </div>
                <div className="cohap-field">
                  <label>Company website</label>
                  <input data-testid="cohap-website" value={form.website} onChange={(e) => update('website', e.target.value)} placeholder="https://…" />
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="cohap-card-h">Tell us about your <em>venture</em>.</h2>
              <p className="cohap-card-sub">Be concrete. We're looking for clarity, not buzzwords.</p>

              <div className="cohap-row">
                <div className="cohap-field">
                  <label>Industry<span className="req">*</span></label>
                  <select data-testid="cohap-industry" value={form.industry} onChange={(e) => update('industry', e.target.value)}>
                    <option value="">Select an industry…</option>
                    {INDUSTRIES.map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                </div>
                <div className="cohap-field">
                  <label>Funding stage<span className="req">*</span></label>
                  <select data-testid="cohap-stage" value={form.funding_stage} onChange={(e) => update('funding_stage', e.target.value)}>
                    <option value="">Select stage…</option>
                    {FUNDING_STAGES.map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                </div>
              </div>

              <div className="cohap-row">
                <div className="cohap-field">
                  <label>Team size</label>
                  <input data-testid="cohap-team" type="number" min={1} value={form.team_size} onChange={(e) => update('team_size', e.target.value)} />
                </div>
                <div />
              </div>

              <div className="cohap-row full">
                <div className="cohap-field">
                  <label>One-liner<span className="req">*</span></label>
                  <input data-testid="cohap-oneliner" value={form.one_liner} onChange={(e) => update('one_liner', e.target.value.slice(0, 240))} placeholder="What does your company do — in one sentence?" />
                  <span className="cohap-field-counter">{form.one_liner.length}/240</span>
                </div>
              </div>

              <div className="cohap-row full">
                <div className="cohap-field">
                  <label>Problem<span className="req">*</span></label>
                  <textarea data-testid="cohap-problem" value={form.problem} onChange={(e) => update('problem', e.target.value)} placeholder="Who is hurting today, why, and how do they solve it now?" />
                </div>
              </div>

              <div className="cohap-row full">
                <div className="cohap-field">
                  <label>Solution<span className="req">*</span></label>
                  <textarea data-testid="cohap-solution" value={form.solution} onChange={(e) => update('solution', e.target.value)} placeholder="What you've built, what's working, and what's different about your approach." />
                </div>
              </div>

              <div className="cohap-row full">
                <div className="cohap-field">
                  <label>Target market<span className="req">*</span></label>
                  <textarea data-testid="cohap-market" value={form.target_market} onChange={(e) => update('target_market', e.target.value)} placeholder="Specific buyer persona, market size, and where you're winning first." />
                </div>
              </div>

              <div className="cohap-row full">
                <div className="cohap-field">
                  <label>Traction so far <span style={{ fontWeight: 400, color: '#7A8190' }}>(optional but recommended)</span></label>
                  <textarea data-testid="cohap-traction" value={form.traction} onChange={(e) => update('traction', e.target.value)} placeholder="Revenue, pilots, LOIs, IP, awards, capital raised, partnerships." />
                </div>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h2 className="cohap-card-h">Why the <em>UAE</em>?</h2>
              <p className="cohap-card-sub">This is where most applications get rejected — show us the strategic reason, not just the lifestyle one.</p>

              <div className="cohap-row full">
                <div className="cohap-field">
                  <label>Why the UAE for your venture?<span className="req">*</span></label>
                  <textarea data-testid="cohap-why-uae" value={form.why_uae} onChange={(e) => update('why_uae', e.target.value)} placeholder="What does the UAE specifically unlock or accelerate? Customers, regulation, capital, talent, sovereignty — be specific." style={{ minHeight: 130 }} />
                  <span className="cohap-field-hint">"We want a Dubai office" doesn't score well. Real regulatory, customer or capital reasons do.</span>
                </div>
              </div>

              <div className="cohap-row full">
                <div className="cohap-field">
                  <label>Governance posture <span style={{ fontWeight: 400, color: '#7A8190' }}>(optional)</span></label>
                  <textarea data-testid="cohap-governance" value={form.governance_focus} onChange={(e) => update('governance_focus', e.target.value)} placeholder="Compliance frameworks, board structure, advisors, certifications, AML/KYC posture." />
                </div>
              </div>

              <div className="cohap-row full">
                <div className="cohap-field">
                  <label>Additional links <span style={{ fontWeight: 400, color: '#7A8190' }}>(optional)</span></label>
                  <textarea data-testid="cohap-links" value={form.additional_links} onChange={(e) => update('additional_links', e.target.value)} placeholder="Pitch deck, demo video, press, anything else we should see." style={{ minHeight: 60 }} />
                </div>
              </div>
            </>
          )}

          <div className="cohap-actions">
            <button
              className="cohap-btn line"
              onClick={back}
              disabled={step === 1 || submitting}
              data-testid="cohap-back-btn"
            >
              <ChevronLeft className="h-3.5 w-3.5" />Back
            </button>

            {step < 3 ? (
              <button className="cohap-btn primary" onClick={next} data-testid="cohap-next-btn">
                Continue<ChevronRight className="h-3.5 w-3.5" />
              </button>
            ) : (
              <button
                className="cohap-btn primary"
                onClick={submit}
                disabled={submitting}
                data-testid="cohap-submit-btn"
              >
                {submitting ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Submitting…</> : <><Sparkles className="h-3.5 w-3.5" />Submit application<ArrowRight className="h-3.5 w-3.5" /></>}
              </button>
            )}
          </div>
        </div>

        <p style={{ fontSize: 12, color: '#7A8190', marginTop: 24, textAlign: 'center', lineHeight: 1.6 }}>
          Submitted applications are AI-reviewed against four axes — governance, traction, founder fit, and UAE-relevance.
          We respond within 7 working days. Your data is private and only used for cohort selection.
        </p>
      </div>
    </div>
  );
}
